import styled, { DefaultTheme } from 'styled-components';
import { Modal, Input, Tabs } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    font-size: 18px;
    font-weight: 600;
  }

  .ant-modal-body {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
`;

export const IconWrapper = styled.div`
  font-size: 20px;
  display: flex;
  align-items: center;
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};

    &:hover {
      color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    }
  }

  .ant-tabs-tab-active {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1890ff')};
  }

  .ant-tabs-ink-bar {
    background: #1890ff;
  }

  .ant-tabs-content {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormLabel = styled.label`
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 8px;
`;

export const StyledInput = styled(Input)`
  background: ${(props) => getThemeValue(props, '#262626', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};

  &::placeholder {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#bfbfbf')};
  }

  &:hover {
    border-color: #1890ff;
    background: ${(props) => getThemeValue(props, '#262626', '#ffffff')};
  }

  &:focus {
    background: ${(props) => getThemeValue(props, '#262626', '#ffffff')};
    border-color: #1890ff;
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  }
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 4px;
  background: ${(props) => getThemeValue(props, '#262626', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  resize: vertical;

  &::placeholder {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#bfbfbf')};
  }

  &:hover {
    border-color: #1890ff;
  }

  &:focus {
    border-color: #1890ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
`;

export const HelpText = styled.div`
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  font-size: 13px;
  line-height: 1.6;
  margin-top: 8px;
`;

export const PreviewBox = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 4px;
  overflow-y: auto;
  max-height: 400px;
`;

export const PreviewTitle = styled.div`
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
`;

export const PreviewText = styled.div`
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const HtmlPreviewBox = styled.div`
  padding: 16px;
  background: #ffffff;
  color: #000000;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 4px;
  overflow-y: auto;
  max-height: 400px;

  img {
    max-width: 100%;
    height: auto;
  }

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const CodeBlock = styled.pre`
  background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  padding: 16px;
  border-radius: 4px;
  overflow: auto;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
`;

export const VariableItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};

  &:last-child {
    border-bottom: none;
  }
`;

export const VariableKey = styled.code`
  background: ${(props) => getThemeValue(props, '#262626', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#ff7a45', '#ff7a45')};
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
`;

export const VariableValue = styled.div`
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  font-size: 13px;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AlertBox = styled.div<{ type: 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 4px;
  margin-top: 12px;
  border-left: 4px solid ${(props) => (props.type === 'error' ? '#ff4d4f' : '#1890ff')};
  background: ${(props) =>
    props.type === 'error'
      ? getThemeValue(props, '#2f1515', '#fff2f0')
      : getThemeValue(props, '#111d2c', '#e6f7ff')};
  color: ${(props) =>
    props.type === 'error'
      ? getThemeValue(props, '#ff7875', '#cf1322')
      : getThemeValue(props, '#91d5ff', '#0050b3')};
  font-size: 13px;
`;

export const DividerStyled = styled.div`
  height: 1px;
  background: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  margin: 16px 0;
`;

export const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

export const BadgeStyled = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: ${(props) => getThemeValue(props, '#111d2c', '#e6f7ff')};
  color: ${(props) => getThemeValue(props, '#91d5ff', '#0050b3')};
  border: 1px solid ${(props) => getThemeValue(props, '#15395b', '#91d5ff')};
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
